import { Fade, Modal, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import { Link as RouterLink } from 'react-router-dom';
import { addInterestedUser } from '../../api/interestedUsers/interestedUsers.api';
// import { getRouteTranslations } from '../../constants';
import { useFormField } from '../../hooks/use-form-field';
import { Toast } from '../Toast';
import * as Styled from './ExitIntentModal.styles';

const BACKDROP_PROPS = {
  timeout: 300,
};

const ExitIntentModal = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [success, setSuccess] = useState(false);
  const [requestError, setRequestError] = useState('');
  // const [consentChecked, setConsentChecked] = useState(false);
  // const [consentTouched, setConsentTouched] = useState(false);
  const isLargeDesc = useMediaQuery(theme.breakpoints.down('xl'));
  // const { privacyPolicy: privacyPolicyPath } = useMemo(
  //   () => getRouteTranslations(),
  //   [],
  // );

  const emailValidator = useCallback(
    (val) => {
      if (!val) return t('errors.required');
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ? '' : t('errors.email');
    },
    [t],
  );

  const { error, value, touched, onBlur, onChange, setTouched, reset } =
    useFormField({
      name: 'email',
      validator: emailValidator,
      onErrorChange: () => {},
    });

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    setIsOpen(false);
  };

  const handleToastClose = () => {
    setRequestError('');
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      // setConsentTouched(true);
      // if (!consentChecked) return;

      if (error) {
        setTouched(true);
        return;
      }

      try {
        await addInterestedUser({ email: value });
        setSuccess(true);
        reset();
        // setConsentChecked(false);
        // setConsentTouched(false);
      } catch (err) {
        setRequestError(err.message);
      }
    },
    [error, reset, setTouched, value],
  );

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown && isDesktop) {
        setIsOpen(true);
        setHasShown(true);
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [hasShown, isDesktop]);

  useEffect(() => {
    const header = document.querySelector('#header');
    const body = document.querySelector('body');
    const headerPadding = window.getComputedStyle(header).paddingRight;

    const observer = new MutationObserver(() => {
      const bodyPadding = parseFloat(body.style.paddingRight) || 0;
      if (header) {
        if (isLargeDesc) {
          header.style.paddingRight = `${parseFloat(headerPadding) + bodyPadding}px`;
        } else {
          header.style.marginLeft = `-${bodyPadding / 2}px`;
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, [isLargeDesc]);

  // const showConsentError = consentTouched && !consentChecked;
  const isSubmitDisabled = !!error || !value; // || !consentChecked;

  return (
    <>
      <Toast
        isOpen={!!requestError}
        onClose={handleToastClose}
        message={requestError}
        innerKey="requestError"
      />
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="exit-intent-modal"
        closeAfterTransition
        disableEnforceFocus
        slots={{ backdrop: Styled.BlurredBackdrop }}
        slotProps={{ backdrop: BACKDROP_PROPS }}
      >
        <Fade in={isOpen}>
          <Styled.Wrapper>
            <Styled.CloseButton
              type="button"
              onClick={handleClose}
              aria-label={t('exitInternalModal.closeAriaLabel')}
            >
              <Styled.CloseIcon variant="closeCircle" />
            </Styled.CloseButton>

            <Styled.Form onSubmit={handleSubmit}>
              <Styled.TextBlock>
                <Styled.Title variant="h3" id="exit-intent-modal">
                  {success
                    ? t('exitInternalModal.success.title')
                    : t('exitInternalModal.title')}
                </Styled.Title>

                <Styled.Description variant="body16">
                  {success
                    ? t('exitInternalModal.success.description')
                    : t('exitInternalModal.description')}
                </Styled.Description>
              </Styled.TextBlock>

              {!success && (
                <Styled.FormFields>
                  <Styled.EmailRow>
                    <Styled.EmailFieldWrapper>
                      <Styled.EmailIcon variant="letter" />
                      <Styled.EmailInput
                        id="exit-intent-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder={t('placeholders.popupEmail')}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={touched && !!error}
                        aria-invalid={touched && !!error}
                        aria-describedby={
                          touched && error
                            ? 'exit-intent-email-error'
                            : undefined
                        }
                      />
                      {touched && error && (
                        <Styled.InputError
                          id="exit-intent-email-error"
                          variant="caption"
                        >
                          {error}
                        </Styled.InputError>
                      )}
                    </Styled.EmailFieldWrapper>

                    <Styled.SubscribeButton
                      type="submit"
                      disabled={isSubmitDisabled}
                    >
                      {t('buttons.subscribe')}
                    </Styled.SubscribeButton>
                  </Styled.EmailRow>

                  {/* <Styled.ConsentRow>
                    <Styled.ConsentCheckbox
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      disableRipple
                      inputProps={{ 'aria-label': t('exitInternalModal.consent') }}
                    />
                    <Styled.ConsentText variant="body2" component="span">
                      {t('exitInternalModal.consent')}
                      <Styled.PrivacyLink
                        component={RouterLink}
                        to={privacyPolicyPath}
                        onClick={handleClose}
                      >
                        {t('labels.privacyPolicy')}
                      </Styled.PrivacyLink>
                      .
                    </Styled.ConsentText>
                  </Styled.ConsentRow>

                  {showConsentError && (
                    <Styled.ConsentError variant="caption">
                      {t('exitInternalModal.consentRequired')}
                    </Styled.ConsentError>
                  )} */}
                </Styled.FormFields>
              )}

              {success && (
                <Styled.SuccessActions>
                  <Styled.OkButton type="button" onClick={handleClose}>
                    {t('exitInternalModal.success.ok')}
                  </Styled.OkButton>
                </Styled.SuccessActions>
              )}
            </Styled.Form>
          </Styled.Wrapper>
        </Fade>
      </Modal>
    </>
  );
};

export default ExitIntentModal;
